import { useNavigate } from 'react-router-dom';
import { quizPercentage } from '../../Helpers/quiz-percentage';
import { addYears, format } from 'date-fns';
import Auth from '../../Helpers/auth';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  httpCompleteCourse,
  httpIncompleteCourse,
} from '../../Hooks/user/user.put.hooks';
import PrimaryButton from '../../Components/Buttons/PrimaryButton/PrimaryButton.component';
import PropTypes from 'prop-types';

// Form for submitting the quiz
const QuizSubmit = ({
  id,
  correct,
  length,
  course,
  answered,
  userAnswers,
  user,
}) => {
  const navigate = useNavigate();
  const [err, setErr] = useState('');
  const { data } = Auth.getProfile();
  const userId = data.id || localStorage.getItem('temp_id');
  const courseId = localStorage.getItem('courseId');
  const courseData = user?.courses?.find((course) => course?._id === courseId);
  const queryClient = useQueryClient();
  // Complete Mutation
  const completeMutation = useMutation({
    // updatedCourse, data.id
    mutationFn: ({ course, userId }) => httpCompleteCourse(course, userId),
    onSuccess: (response) => {
      if (response.status === 200 || response.status === 201) {
        queryClient.invalidateQueries(['course', id]);
        navigate(`/course/quiz/complete/${id}`);
      } else if (response.status === 400) {
        setErr('Bad Request: Unable to submit the quiz.');
      } else if (response.status === 500) {
        setErr('Server Error: Please try again later.');
      } else {
        setErr('Unexpected response from the server.');
      }
    },
    onError: async (error) => {
      let err = await error.json();
      console.error('Error within completing course:', error);
      setErr(err.msg || 'Failed to complete course');
    },
  });
  // Incomplete Mutation
  const incompleteMutation = useMutation({
    mutationFn: ({ course, userId }) => httpIncompleteCourse(course, userId),
    onSuccess: (response) => {
      if (response.status === 200 || response.status === 201) {
        queryClient.invalidateQueries(['course', id]);
        navigate(`/course/quiz/incomplete/${id}`);
      } else if (response.status === 400) {
        setErr('Bad Request: Unable to submit the quiz.');
      } else if (response.status === 500) {
        setErr('Server Error: Please try again later.');
      } else {
        setErr('Unexpected response from the server.');
      }
    },
    onError: async (error) => {
      let err = await error.json();
      console.error('Error incomplete course:', error);
      setErr(err.msg || 'Failed to set incomplete course');
    },
  });

  const submitQuiz = async () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(`quizAnswers-`)) {
        localStorage.removeItem(key);
      }
    });
    let isCompleted = quizPercentage(correct, length);
    if (isCompleted >= 85) {
      let date = Date.now();
      const dateFormatted = format(new Date(), 'MMMM dd, yyyy');
      let updatedCourse = {
        ...courseData,
        certNum: `${course.certificatePrefix}${String(course.certificateCount).padStart(4, '0')}`,
        complete: true,
        score: isCompleted,
        date: dateFormatted,
        userAnswers,
      };
      if (course?.expiryDate) {
        let expiryDate = addYears(date, course.expiryDate);
        let expired = format(expiryDate, 'MMMM dd, yyyy');
        updatedCourse.expiryDate = expired;
        updatedCourse.expiredDateLong = expiryDate.getTime();
      } else {
        updatedCourse.expiryDate = '';
        updatedCourse.expiredDateLong = '';
      }
      completeMutation.mutate({
        course: updatedCourse,
        userId: userId,
      });
    } else {
      let updatedCourse = {
        ...courseData,
        userAnswers: userAnswers,
        complete: false,
        score: isCompleted,
      };
      incompleteMutation.mutate({
        course: updatedCourse,
        userId: userId,
      });
    }
  };

  return (
    <div
      className="column"
      style={{
        textAlign: 'center',
      }}
    >
      {err && <p className="error">{err}</p>}
      <PrimaryButton
        functionName={() => submitQuiz(id)}
        span="Submit Quiz"
        disabled={answered === length ? false : true}
      />
      <p>
        You have answered {answered} / {length} questions
      </p>
      <span>
        If the button is not yellow you have not answered all the questions
      </span>
    </div>
  );
};
QuizSubmit.propTypes = {
  id: PropTypes.string,
  correct: PropTypes.number,
  length: PropTypes.number,
  answered: PropTypes.number,
  userAnswers: PropTypes.object,
  user: PropTypes.shape({
    courses: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
      })
    ),
  }),
  course: PropTypes.shape({
    _id: PropTypes.string,
    certificateCount: PropTypes.number,
    certificatePrefix: PropTypes.string,
    expiryDate: PropTypes.number,
  }),
};

export default QuizSubmit;
